// src/utils/paystack.js
/**
 * Paystack Payment Configuration and Utilities
 * Handles credit purchases and payment verification
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Paystack configuration
export const PAYSTACK_CONFIG = {
  publicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
  currency: 'ZAR',
  channels: ['card', 'bank', 'ussd', 'qr', 'mobile_money', 'bank_transfer']
};

// Credit packages configuration
export const CREDIT_PACKAGES = [
  {
    id: 'individual_5',
    credits: 5,
    price: 10,
    pricePerCredit: 2.00,
    popular: false,
    savings: null,
    description: 'Pay as you go'
  },
  {
    id: 'individual_10',
    credits: 10,
    price: 20,
    pricePerCredit: 2.00,
    popular: false,
    savings: null,
    description: 'Small batch'
  },
  {
    id: 'starter',
    credits: 25,
    price: 40,
    pricePerCredit: 1.60,
    popular: false,
    savings: 'Save R10',
    description: 'Best value for small businesses'
  },
  {
    id: 'popular',
    credits: 100,
    price: 140,
    pricePerCredit: 1.40,
    popular: true,
    savings: 'Save R60',
    description: 'Most popular choice'
  }
];

/**
 * Initialize Paystack payment
 * @param {Object} paymentData - Payment configuration
 * @param {Function} onSuccess - Success callback
 * @param {Function} onClose - Close callback
 */
export const initializePaystackPayment = async (paymentData, onSuccess, onClose) => {
  try {
    // Dynamic import to avoid bundle issues
    const PaystackPop = (await import('@paystack/inline-js')).default;
    
    const paystack = new PaystackPop();
    
    const config = {
      key: PAYSTACK_CONFIG.publicKey,
      email: paymentData.email,
      amount: paymentData.amount * 100, // Convert to cents (ZAR)
      currency: PAYSTACK_CONFIG.currency,
      ref: generatePaymentReference(paymentData.userId),
      channels: PAYSTACK_CONFIG.channels,
      metadata: {
        userId: paymentData.userId,
        credits: paymentData.credits,
        packageId: paymentData.packageId,
        userEmail: paymentData.email,
        custom_fields: [
          {
            display_name: "Credit Package",
            variable_name: "credit_package",
            value: `${paymentData.credits} credits`
          }
        ]
      },
      callback: function(response) {
        console.log('Paystack payment successful:', response);
        onSuccess(response);
      },
      onClose: function() {
        console.log('Paystack payment modal closed');
        onClose();
      }
    };

    paystack.newTransaction(config);
  } catch (error) {
    console.error('Error initializing Paystack payment:', error);
    throw new Error('Failed to initialize payment. Please try again.');
  }
};

/**
 * Generate unique payment reference
 * @param {string} userId - User ID
 * @returns {string} Unique payment reference
 */
export const generatePaymentReference = (userId) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `credits_${userId.substring(0, 8)}_${timestamp}_${random}`;
};

/**
 * Wait for webhook to process payment and add credits
 * @param {Object} paymentResponse - Paystack payment response
 * @param {Object} paymentData - Original payment data
 * @returns {Promise<boolean>} Success status
 */
export const waitForWebhookProcessing = async (paymentResponse, paymentData) => {
  const { userId, credits } = paymentData;
  const maxWaitTime = 300000; // 5 minutes (live payments can take longer than test payments)
  const pollInterval = 2000; // 2 seconds
  const startTime = Date.now();
  
  console.log('⏳ Waiting for webhook to process payment:', {
    reference: paymentResponse.reference,
    expectedCredits: credits,
    userId: userId.substring(0, 8) + '...'
  });

  // Get initial credit balance
  const userRef = doc(db, 'users', userId);
  const initialDoc = await getDoc(userRef);
  const initialCredits = initialDoc.exists() ? initialDoc.data().credits || 0 : 0;
  
  const expectedCredits = initialCredits + credits;

  return new Promise((resolve, reject) => {
    const checkCredits = async () => {
      try {
        const currentDoc = await getDoc(userRef);
        const currentCredits = currentDoc.exists() ? currentDoc.data().credits || 0 : 0;
        
        if (currentCredits >= expectedCredits) {
          console.log(`✅ Webhook processed payment successfully. Credits: ${initialCredits} → ${currentCredits}`);
          resolve(true);
          return;
        }
        
        // Check if we've exceeded max wait time
        if (Date.now() - startTime >= maxWaitTime) {
          console.error('⏰ Webhook processing timeout');
          reject(new Error('Payment processing is taking longer than expected. Your payment was successful, but credits may take a few minutes to appear. Please refresh the page or contact support if credits don\'t appear within 10 minutes.'));
          return;
        }
        
        // Continue polling
        setTimeout(checkCredits, pollInterval);
        
      } catch (error) {
        console.error('❌ Error checking credit balance:', error);
        reject(new Error('Unable to verify credit addition. Please refresh the page or contact support.'));
      }
    };
    
    // Start polling after a short delay to allow webhook to process
    setTimeout(checkCredits, 1000);
  });
};

/**
 * Verify payment with Paystack (for webhook usage)
 * @param {string} reference - Payment reference
 * @returns {Object} Verification result
 */
export const verifyPayment = async (reference) => {
  // This would typically be done on the backend
  // For now, we'll rely on the frontend callback
  console.log('Payment verification requested for:', reference);
  return { verified: true, reference };
};

/**
 * Get package details by ID
 * @param {string} packageId - Package ID
 * @returns {Object|null} Package details
 */
export const getPackageById = (packageId) => {
  return CREDIT_PACKAGES.find(pkg => pkg.id === packageId) || null;
};

/**
 * Calculate discount percentage
 * @param {number} originalPrice - Original price per credit
 * @param {number} discountedPrice - Discounted price per credit
 * @returns {number} Discount percentage
 */
export const calculateDiscountPercentage = (originalPrice, discountedPrice) => {
  return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
};

/**
 * Format currency for display
 * @param {number} amount - Amount in rands
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return `R${amount.toFixed(2)}`;
};

// Environment variable validation
export const validatePaystackConfig = () => {
  if (!PAYSTACK_CONFIG.publicKey) {
    console.error('REACT_APP_PAYSTACK_PUBLIC_KEY environment variable is not set');
    return false;
  }
  
  // Paystack mode detection (removed console warning to avoid confusion)
  
  return true;
};